// WF-FIX (2026-10-26): DE › Overview › "Agents": the agents (AG) that report to this DE, each a
// link to its own page (`/agents/ag/{id}`) with its status and latest activity, and — for whoever
// may change it — a visible Reassign on the row (`ReassignItems`, the same choices as the Agents
// list). Reads the cached agents list (a shell read: the sidebar shares it), so no extra call;
// the Reassign choices load only when the viewer may change one of them.
import {
  Badge,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
  SettingsSection,
  WorkerBadge,
  workerName,
} from "@companyos/ui";
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { ArrowRightLeftIcon, WorkflowIcon } from "lucide-react";
import type { Api } from "../../api/client";
import type { DeProfile, WorkforceAgentRow } from "../../api/types";
import { keepFocus } from "../../focus/focus";
import {
  AG_STATUS,
  type AgChoices,
  activityLabel,
  ReassignItems,
  useAgChange,
  useAgChoices,
} from "../agents/ag-actions";
import type { CompanyAccess } from "../company";
import { workforceAgentsQuery } from "../queries";
import { LoadError } from "../screen-parts";

export function DeReportingAgents({
  api,
  company,
  de,
}: {
  api: Api;
  company: CompanyAccess;
  de: Pick<DeProfile, "membership_id" | "display_name">;
}) {
  const ags = useQuery(workforceAgentsQuery(api, company.id));
  const rows = (ags.data ?? []).filter((a) => a.reports_to_membership_id === de.membership_id);
  const choices = useAgChoices(
    api,
    company.id,
    rows.some((a) => a.can_edit),
  );
  return (
    <SettingsSection
      icon={WorkflowIcon}
      // HELPERS: the one noun for the agents (AG) that report to it.
      title="Helpers"
      description={
        ags.isSuccess
          ? rows.length
            ? `${rows.length} report to ${workerName(de.display_name)}`
            : "No helper reports to it"
          : undefined
      }
      data-slot="de-reporting-agents"
    >
      {ags.isError ? (
        <LoadError what="Agents" error={ags.error} />
      ) : rows.length ? (
        <ul className="m-0 flex list-none flex-col p-0">
          {rows.map((a) => (
            <AgLine key={a.id} api={api} company={company} agent={a} choices={choices} />
          ))}
        </ul>
      ) : null}
    </SettingsSection>
  );
}

function AgLine({
  api,
  company,
  agent: a,
  choices,
}: {
  api: Api;
  company: CompanyAccess;
  agent: WorkforceAgentRow;
  choices: AgChoices;
}) {
  const change = useAgChange(api, company.id, a);
  const activity = activityLabel(a);
  const name = workerName(a.name);
  return (
    <li
      className="group/ag flex min-h-9 items-center gap-2 border-b border-subtle py-1 last:border-b-0"
      data-ag={a.id}
    >
      <WorkerBadge kind="ag" size="sm" />
      <Link
        to="/c/$slug/agents/ag/$id"
        params={{ slug: company.slug, id: a.id }}
        search={keepFocus}
        className="min-w-0 truncate text-sm text-foreground hover:underline"
      >
        {name}
      </Link>
      <Badge variant={AG_STATUS[a.status].variant}>{AG_STATUS[a.status].label}</Badge>
      <span
        className="ml-auto min-w-0 truncate text-xs text-muted-foreground max-sm:hidden"
        title={activity?.full ?? undefined}
      >
        {activity ? [activity.text, activity.when].filter(Boolean).join(" · ") : null}
      </span>
      {a.can_edit ? (
        <DropdownMenu>
          <DropdownMenuTrigger
            render={
              <Button
                variant="ghost"
                size="xs"
                aria-label={`Reassign ${name}`}
                disabled={change.isPending}
                className="shrink-0"
              />
            }
          >
            <ArrowRightLeftIcon data-icon="inline-start" />
            Reassign
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="min-w-48">
            <ReassignItems
              choices={choices}
              current={a.reports_to_membership_id}
              onPick={(id) => change.mutate({ reports_to_membership_id: id })}
            />
          </DropdownMenuContent>
        </DropdownMenu>
      ) : null}
    </li>
  );
}
